import React from 'react';
import { Search, LayoutGrid, List, ArrowUpDown, ChevronsUpDown, ChevronsDownUp, X } from 'lucide-react';
import { Input } from '../../../../../components/ui/Input';
import { FilterDropdown } from './FilterDropdown';
import { FilterState, ViewMode } from './types';
import type { SortMode } from './useRegistryFilters';
interface RegistryToolbarProps {
  title: string;
  icon: React.ReactNode;
  activeCount: number;
  totalCount: number;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  availableModules: string[];
  accentColor?: 'blue' | 'purple';
  sortMode?: SortMode;
  onSortModeChange?: (mode: SortMode) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  filteredCount?: number;
}
export function RegistryToolbar({
  title,
  icon,
  activeCount,
  totalCount,
  filters,
  onFiltersChange,
  viewMode,
  onViewModeChange,
  availableModules,
  accentColor = 'blue',
  sortMode = 'usage',
  onSortModeChange,
  onExpandAll,
  onCollapseAll,
  hasActiveFilters = false,
  onClearFilters,
  filteredCount
}: RegistryToolbarProps) {
  const moduleOptions = availableModules.map(m => ({
    id: m,
    label: m
  }));
  const statusOptions = [{
    id: 'active',
    label: 'Actief in gebruik'
  }, {
    id: 'unused',
    label: 'Ongebruikt'
  }, {
    id: 'unregistered',
    label: 'Niet geregistreerd'
  }];
  const accentText = accentColor === 'blue' ? 'text-blue-600' : 'text-purple-600';
  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4 sm:mb-6 sticky top-0 z-20" data-id="element-5188">
      <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-100 bg-gray-50 flex flex-col gap-3" data-id="element-5189">
        {/* Title row */}
        <div className="flex items-center justify-between gap-3" data-id="element-5190">
          <div className="min-w-0 flex-1" data-id="element-5191">
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 flex items-center gap-2 truncate" data-id="element-5192">
              {icon}
              <span className="truncate" data-id="element-5193">{title}</span>
            </h3>
            <p className="text-[11px] sm:text-sm text-gray-500 mt-0.5" data-id="element-5194">
              {activeCount} van {totalCount} actief
              {hasActiveFilters && typeof filteredCount === 'number' && <span className={`ml-1.5 font-medium ${accentText}`} data-id="element-5195">
                  · {filteredCount}{' '}
                  {filteredCount === 1 ? 'resultaat' : 'resultaten'}
                </span>}
            </p>
          </div>

          {/* View toggle — always visible top-right */}
          <div className="flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200 shrink-0" data-id="element-5196">
            <button onClick={() => onViewModeChange('cards')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Kaartenweergave" aria-label="Kaartenweergave" data-id="element-5197">
              <LayoutGrid className="w-4 h-4" data-id="element-5198" />
            </button>
            <button onClick={() => onViewModeChange('compact')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'compact' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Compacte weergave" aria-label="Compacte weergave" data-id="element-5199">
              <List className="w-4 h-4" data-id="element-5200" />
            </button>
          </div>
        </div>

        {/* Controls row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-id="element-5201">
          {/* Search */}
          <div className="w-full sm:flex-1 sm:max-w-xs" data-id="element-5202">
            <Input type="text" placeholder="Zoeken..." value={filters.searchQuery} onChange={e => onFiltersChange({
            ...filters,
            searchQuery: e.target.value
          })} icon={<Search className="w-4 h-4" data-id="element-5204" />} variant="admin" size="sm" data-id="element-5203" />
          </div>

          {/* Filters + Sort + Expand controls */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap" data-id="element-5205">
            <FilterDropdown label="Modules" options={moduleOptions} selectedValues={filters.modules} onChange={modules => onFiltersChange({
            ...filters,
            modules
          })} badgeColor={accentColor} data-id="element-5206" />
            <FilterDropdown label="Status" options={statusOptions} selectedValues={filters.status} onChange={status => onFiltersChange({
            ...filters,
            status: status as any
          })} badgeColor={accentColor} data-id="element-5207" />

            {onSortModeChange && <button onClick={() => onSortModeChange(sortMode === 'usage' ? 'alpha' : 'usage')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-xs sm:text-sm font-medium text-gray-700 transition-colors" title={sortMode === 'usage' ? 'Sorteren op gebruik (klik voor A-Z)' : 'Alfabetisch (klik voor gebruik)'} data-id="element-5208">
                <ArrowUpDown className="w-3.5 h-3.5 text-gray-500" data-id="element-5209" />
                <span className="hidden sm:inline" data-id="element-5210">
                  {sortMode === 'usage' ? 'Gebruik' : 'A-Z'}
                </span>
              </button>}

            {/* Expand / collapse all */}
            {(onExpandAll || onCollapseAll) && <div className="hidden md:flex items-center gap-0.5 border border-gray-200 rounded-lg bg-white" data-id="element-5211">
                {onExpandAll && <button onClick={onExpandAll} className="p-1.5 hover:bg-gray-50 rounded-l-lg transition-colors text-gray-500 hover:text-gray-700" title="Alles uitklappen" aria-label="Alles uitklappen" data-id="element-5212">
                    <ChevronsUpDown className="w-3.5 h-3.5" data-id="element-5213" />
                  </button>}
                {onCollapseAll && <button onClick={onCollapseAll} className="p-1.5 hover:bg-gray-50 rounded-r-lg transition-colors text-gray-500 hover:text-gray-700 border-l border-gray-200" title="Alles inklappen" aria-label="Alles inklappen" data-id="element-5214">
                    <ChevronsDownUp className="w-3.5 h-3.5" data-id="element-5215" />
                  </button>}
              </div>}

            {hasActiveFilters && onClearFilters && <button onClick={onClearFilters} className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium ${accentText} hover:underline transition-colors`} title="Filters wissen" data-id="element-5216">
                <X className="w-3.5 h-3.5" data-id="element-5217" />
                <span data-id="element-5218">Wissen</span>
              </button>}
          </div>
        </div>
      </div>
    </div>;
}